/**
 * See physical-store.json for API details.
 * integration/specs/custom/physical-store.json
 */
import { getServerCacheScope } from '@/data/cache/getServerCacheScope';
import { GET_PHYSICAL_STORE_KEY } from '@/data/constants/hdmDataKey';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { getSettings, useSettings } from '@/data/Settings';
import { Cache } from '@/data/types/Cache';
import { getUser } from '@/data/User-Server';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getRequestId } from '@/data/utils/getRequestId';
import { getServerSideCommon } from '@/data/utils/getServerSideCommon';
import { shrink } from '@/data/utils/keyUtil';
import { laggyMiddleWare } from '@/data/utils/laggyMiddleWare';
import { traceWithId } from '@/data/utils/loggerUtil';
import { customPhysicalStore } from 'integration/generated/custom';

import { PhysicalStoreResponse } from 'integration/generated/custom/data-contracts';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { GetServerSidePropsContext } from 'next';
import useSWR, { unstable_serialize } from 'swr';

const getPhysicalStoreSWRKey = (storeId: string) => ({
	storeId,
});

export const physicalStoreFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		query: {
			responseFormat?: 'json' | 'xml';
		},
		params: RequestParams
	): Promise<PhysicalStoreResponse> => {
		const res = await customPhysicalStore(pub).physicalStoreFindAll(storeId, query, params);
		return res;
	};

/**
 * Server-side function to fetch all physical stores.
 * This function is intended to be used in `getServerSideProps` and leverages the server-side cache.
 *
 * @param cache The server-side cache instance.
 * @param context The `GetServerSidePropsContext` object.
 * @returns The physical stores data or null if an error occurs.
 */
export const getAllPhysicalStores = async (
	cache: Cache,
	context: GetServerSidePropsContext
): Promise<PhysicalStoreResponse | null> => {
	const requestId = getRequestId(context);
	traceWithId(requestId, `getPhysicalStore: start`);
	const settings = await getSettings(cache, context);
	const user = await getUser(cache, context);
	const { storeId } = getServerSideCommon(settings, context);
	if (!storeId) {
		traceWithId(requestId, `getPhysicalStore: end (missing storeId)`);
		return null;
	}
	const props = getPhysicalStoreSWRKey(storeId);
	const key = unstable_serialize([shrink(props), GET_PHYSICAL_STORE_KEY]);
	const cacheScope = getServerCacheScope(context, user.context);

	const value = await cache.get(key);
	if (value) {
		traceWithId(requestId, `getPhysicalStore: end (from cache)`);
		return value;
	}

	try {
		const response = await physicalStoreFetcher(false)(
			storeId,
			{
				responseFormat: 'json',
			},
			{}
		);

		if (response) {
			cache.set(key, response, cacheScope);
			traceWithId(requestId, `getPhysicalStore: end (filled cache)`);
		}
		return response;
	} catch (error) {
		console.error(`Failed to fetch physical stores:`, error);
		traceWithId(requestId, `getPhysicalStore: end (with error)`);
		return null;
	}
};

/**
 * Client-side React hook to fetch all physical stores using SWR.
 *
 * @returns An object containing the physical stores data, loading state, and error state.
 */
export const useAllPhysicalStores = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const key = getPhysicalStoreSWRKey(storeId);
	const { data, error, ...rest } = useSWR<PhysicalStoreResponse>(
		storeId ? [shrink(key), GET_PHYSICAL_STORE_KEY] : null,
		() => physicalStoreFetcher(true)(storeId, { responseFormat: 'json' }, params),
		{ use: [laggyMiddleWare] }
	);

	return {
		physicalStores: data,
		isError: error,
		loading: !error && !data,
		...rest,
	};
};
